<template>
  <OverlayFrame
    :zIndex="overlayZIndex"
    title="Traveling"
    :subtitle="subtitle"
    :icon="ICONS.actions.travel"
    closeLabel="Close travel status"
    @close="emit('close')"
  >
    <div class="stack tight">
      <UiCallout variant="info">
        <div class="row wrap" style="justify-content: space-between; gap: 0.75rem;">
          <div class="stack tight">
            <div class="travel-route">
              {{ fromName }} → {{ toName }}
            </div>
            <div class="text-muted">
              Entering the road… (syncing area data)
            </div>
          </div>
          <div class="spinner" aria-hidden="true"></div>
        </div>
      </UiCallout>

      <div class="travel-meta">
        <div>
          <div class="label text-muted">ETA</div>
          <div class="value stat-mono num">{{ formatMs(remainingMs) }}</div>
        </div>
        <div>
          <div class="label text-muted">Status</div>
          <div class="value">{{ isPaused ? "Resting" : "Walking" }}</div>
        </div>
      </div>

      <div class="actions">
        <UiButton size="sm" variant="secondary" @click="emit('close')">Hide</UiButton>
      </div>
    </div>
  </OverlayFrame>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from "vue";
import OverlayFrame from "../../../components/overlay/OverlayFrame.vue";
import UiButton from "../../../components/ui/UiButton.vue";
import UiCallout from "../../../components/ui/UiCallout.vue";
import { ICONS } from "../../../lib/icons";
import { formatMs } from "../../../lib/format";

const props = withDefaults(
  defineProps<{
    overlayZIndex?: number;
    fromName: string;
    toName: string;
    arrivalAt: string;
    isPaused: boolean;
  }>(),
  {
    overlayZIndex: 4000
  }
);

const emit = defineEmits<{ close: [] }>();

const subtitle = computed(() => `Going to ${props.toName}`);

const remainingMs = ref(0);
let timer: number | null = null;

function recomputeRemaining() {
  const arrival = Date.parse(props.arrivalAt);
  if (!Number.isFinite(arrival)) {
    remainingMs.value = 0;
    return;
  }
  remainingMs.value = Math.max(0, arrival - Date.now());
}

onMounted(() => {
  recomputeRemaining();
  timer = window.setInterval(recomputeRemaining, 250);
});

onUnmounted(() => {
  if (timer != null) window.clearInterval(timer);
  timer = null;
});
</script>

<style scoped>
.spinner {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid color-mix(in srgb, var(--border-0) 55%, transparent);
  border-top-color: var(--accent-travel);
  animation: spin 0.9s linear infinite;
}

.travel-route {
  font-weight: 750;
}

.travel-meta {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.8rem;
  margin-top: 0.25rem;
}

.label {
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.1em;
}

.value {
  margin-top: 0.2rem;
  font-weight: 650;
}

.actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.6rem;
  flex-wrap: wrap;
  margin-top: 0.25rem;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>

